import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './styles.module.scss';
import EventCard from '../EventCard';

type EventData = {
  imageUrl: string;
  title: string;
  time: string;
  location: string;
  description: string;
  labels: string[];
};

type Props = {
  events: EventData[];
  className?: string;
};

const PicupEvent: React.FC<Props> = ({ events, className }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // labelsから日付（11/2 or 11/3）を抽出
  const getDateFromLabels = (labels: string[]) => {
    const date = labels.find((label) => label === '11/2' || label === '11/3') || '';
    return date.replace('/', '.');
  };

  const openModal = (event: EventData) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    document.body.style.overflow = 'unset';
    // Headerを再レンダリングするためにイベントを発行
    window.dispatchEvent(new CustomEvent('modalClosed'));
  };

  if (!events || events.length === 0) return null;

  return (
    <>
      <div className={`${styles.picupEvent} ${className || ''}`}>
        <Swiper
          modules={[Pagination, Autoplay]}
          slidesPerView={1.6}
          centeredSlides={true}
          slideToClickedSlide={true}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
            el: `.${styles.dots}`,
            bulletClass: styles.dot,
            bulletActiveClass: styles.dotActive,
            renderBullet: (index: number, className: string) => {
              return `<span class="${className}" data-index="${index}"></span>`;
            },
          }}
          breakpoints={{
            768: {
              slidesPerView: 2.5,
            },
            1024: {
              slidesPerView: 3.5,
            },
            1440: {
              slidesPerView: 4.5,
            },
          }}
          onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
          className={styles.swiperContainer}
        >
          {events.map((event, index) => {
            const isActive = index === activeIndex;

            return (
              <SwiperSlide key={index} className={styles.swiperSlide}>
                {({ isActive: isSlideActive }) => (
                  <>
                    <div className={styles.imageCards}>
                      <div
                        className={`${styles.imageCard} ${isSlideActive ? styles.imageCardActive : ''}`}
                        onClick={() => openModal(event)}
                      >
                        <div className={styles.dateLabel}>
                          <span className={styles.dateText}>{getDateFromLabels(event.labels)}</span>
                        </div>
                        <img src={event.imageUrl} alt={event.title} className={styles.cardImage} />
                      </div>
                    </div>

                    {isActive && (
                      <div className={styles.content}>
                        <h3 className={styles.title}>{event.title}</h3>
                        <div className={styles.description}>
                          <p>{event.description.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                        </div>
                        <button className={styles.detailButton} onClick={() => openModal(event)}>
                          詳しく見る
                        </button>
                      </div>
                    )}
                  </>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>

        {events.length > 1 && <div className={styles.dots}></div>}
      </div>

      {isModalOpen && selectedEvent && typeof window !== 'undefined'
        ? createPortal(
            <div className={styles.modalOverlay} onClick={closeModal}>
              <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <EventCard
                  events={[selectedEvent]}
                  onClose={closeModal}
                  className={styles.eventModal}
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default PicupEvent;
